import { NextRouter } from "next/router";

import { CompanyServiceType } from "@vahak/core/dist/constants/serviceTypeId";
import { MARKET_TYPE } from "@vahak/core/dist/constants/market";
import { MY_LOAD_LORRY_TABS } from "@vahak/core/dist/constants/tab";
import { ROUTES } from "@vahak/core/dist/constants/routeStrings";

import { getLoggedInDeepLinkHref } from "../../../constants/deep-links";
import { LoginFormAuthResult } from "./loginFormAuth.types";

interface HandlePostLoginNavigationParams {
    router: NextRouter;
    status: LoginFormAuthResult;
    setShouldPreventLayoutSwitch?: (value: boolean) => void;
}

function isIosWebKit(): boolean {
    if (typeof window === "undefined") {
        return false;
    }

    return /iPhone|iPad|iPod/i.test(window.navigator.userAgent);
}

function isMarketingHomeRoute(router: NextRouter): boolean {
    const pathWithoutHash = router.asPath.split("#")[0].split("?")[0];

    return router.route === "/" || pathWithoutHash === "/" || pathWithoutHash === "";
}

function redirectToDashboard(router: NextRouter, setShouldPreventLayoutSwitch?: (value: boolean) => void) {
    setShouldPreventLayoutSwitch?.(false);

    const dashboardPath = ROUTES.dashboard.getUrl();

    // iOS Safari often keeps #Login and stale router state with client-side hash routing.
    if (isIosWebKit()) {
        window.location.assign(dashboardPath);
        return;
    }

    router.replace(
        {
            pathname: dashboardPath,
            hash: undefined
        },
        undefined,
        { shallow: false }
    );
}

export async function handlePostLoginNavigation({
    router,
    status,
    setShouldPreventLayoutSwitch
}: HandlePostLoginNavigationParams): Promise<void> {
    const deepLinkHref = getLoggedInDeepLinkHref(router.query, router.asPath);
    if (deepLinkHref) {
        setShouldPreventLayoutSwitch?.(false);
        window.location.assign(deepLinkHref);
        return;
    }

    const isShipper = status.serviceType === CompanyServiceType.SHIPPER;

    if (isShipper) {
        redirectToDashboard(router, setShouldPreventLayoutSwitch);
        return;
    }

    if (isMarketingHomeRoute(router)) {
        redirectToDashboard(router, setShouldPreventLayoutSwitch);
        return;
    }

    if (!router.asPath.includes("marketplace")) {
        redirectToDashboard(router, setShouldPreventLayoutSwitch);
        return;
    }

    const routeArray = router.asPath.split("#")[0].split("?")[0].split("/");
    if (status.serviceType === CompanyServiceType.FLEET_OWNER) {
        const indexToReplace = routeArray.indexOf("lorry");
        if (indexToReplace !== -1) {
            routeArray[indexToReplace] = "load";
            router.replace(
                {
                    pathname: routeArray.join("/"),
                    hash: undefined
                },
                undefined,
                { shallow: false }
            );
        } else {
            router.replace(
                {
                    pathname: routeArray.join("/"),
                    hash: undefined
                },
                undefined,
                { shallow: true }
            );
        }
    } else {
        const mktQuery = router.query["params"]?.[0];
        const isLorryMkt = mktQuery === MARKET_TYPE.Lorry;
        if (isLorryMkt) {
            const pathname = ROUTES.myLoadsMyLorries.getUrl("lorries", MY_LOAD_LORRY_TABS.LISTING);
            router.replace(
                {
                    pathname: pathname,
                    hash: undefined
                },
                undefined,
                { shallow: true }
            );
        } else {
            router.replace(
                {
                    pathname: routeArray.join("/"),
                    hash: undefined
                },
                undefined,
                { shallow: true }
            );
        }
    }

    setShouldPreventLayoutSwitch?.(false);
}
